import { translate, translateRich, useLocale } from "../../localization";
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ZAxis,
  Cell,
} from "recharts";
import "./EngagementScatter.scss";

export default function EngagementScatter({ sessions = [], onSessionClick }) {
  const locale = useLocale();
  const scatterData = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    return sessions
      .filter(s => s.secondsSpent > 0 && s.meanScore != null)
      .map((s, idx) => ({
        sessionCode: s.sessionCode || s.code || translate("Session {value0}", {
  value0: idx
}),
        timeMinutes: Math.round((s.secondsSpent || 0) / 60),
        score: Number(s.meanScore || 0),
        responseCount: s.responseCount || 0,
        name: translate("Session {value0}", {
  value0: s.sessionCode?.slice(0, 8) || idx
}),
      }));
  }, [sessions, locale]);

  const getColor = (score) => {
    if (score >= 80) return "rgba(16, 185, 129, 0.65)";
    if (score >= 60) return "rgba(251, 191, 36, 0.65)";
    return "rgba(239, 68, 68, 0.65)";
  };

  const CustomTooltip = ({ active, payload }) => {
  useLocale();
    if (!active || !payload || !payload[0]) return null;

    const data = payload[0].payload;
    return (
      <div className="scatter-tooltip">
        <div className="tooltip-session">{data.sessionCode}</div>
        <div className="tooltip-row">
          <span className="tooltip-label">{translate("Time:")}</span>
          <span className="tooltip-value">{translateRich("{value0} min", {
  value0: data.timeMinutes
})}</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">{translate("Score:")}</span>
          <span className="tooltip-value">{Math.round(data.score)}%</span>
        </div>
        <div className="tooltip-row">
          <span className="tooltip-label">{translate("Insights:")}</span>
          <span className="tooltip-value">{data.responseCount}</span>
        </div>
        <div className="tooltip-hint">{translate("Click to view session \u2192")}</div>
      </div>
    );
  };

  if (scatterData.length === 0) {
    return (
      <div className="scatter-empty">{translate("No session data available for visualization.")}</div>
    );
  }

  return (
    <div className="engagementScatter">
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />

          <XAxis
            type="number"
            dataKey="timeMinutes"
            name={translate("Time")}
            unit={translate(" min")}
            tick={{ fontSize: 12, fill: "rgba(0,0,0,0.60)" }}
            label={{ 
              value: "Time Spent (minutes)", 
              position: "insideBottom", 
              offset: -10,
              style: { fontSize: 13, fill: "rgba(0,0,0,0.65)" }
            }}
          />

          <YAxis
            type="number"
            dataKey="score"
            name={translate("Score")}
            unit={translate("%")}
            domain={[0, 100]}
            tick={{ fontSize: 12, fill: "rgba(0,0,0,0.60)" }}
            label={{ 
              value: "Average Score (%)", 
              angle: -90, 
              position: "insideLeft",
              style: { fontSize: 13, fill: "rgba(0,0,0,0.65)" }
            }}
          />

          <ZAxis 
            type="number" 
            dataKey="responseCount" 
            range={[100, 600]} 
            name={translate("Insights")}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: "3 3" }} />

          <Scatter
            data={scatterData}
            onClick={(data) => onSessionClick && onSessionClick(data.sessionCode)}
            style={{ cursor: onSessionClick ? "pointer" : "default" }}
          >
            {scatterData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.score)} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      <div className="scatter-insights">
        <div className="insight-item">
          <div className="insight-icon" style={{ background: "rgba(16, 185, 129, 0.15)" }}>
            <div className="insight-dot" style={{ background: "rgba(16, 185, 129, 0.8)" }} />
          </div>
          <div className="insight-text">{translateRich("{value0} (80%+ score)", {
  value0: <strong>{translate("Top Performers")}</strong>
})}</div>
        </div>

        <div className="insight-item">
          <div className="insight-icon" style={{ background: "rgba(251, 191, 36, 0.15)" }}>
            <div className="insight-dot" style={{ background: "rgba(251, 191, 36, 0.8)" }} />
          </div>
          <div className="insight-text">{translateRich("{value0} (60-80% score)", {
  value0: <strong>{translate("Developing")}</strong>
})}</div>
        </div>

        <div className="insight-item">
          <div className="insight-icon" style={{ background: "rgba(239, 68, 68, 0.15)" }}>
            <div className="insight-dot" style={{ background: "rgba(239, 68, 68, 0.8)" }} />
          </div>
          <div className="insight-text">{translateRich("{value0} (<60% score)", {
  value0: <strong>{translate("Needs Support")}</strong>
})}</div>
        </div>
      </div>

      <div className="scatter-note">{translateRich("Bubble size indicates number of insights completed.{value0}", {
  value0: onSessionClick && translate(" Click any bubble to view session details.")
})}</div>
    </div>
  );
}